// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type AvailableOptions,
  OptionsSelector,
} from '@shared/ui-kit/components/options-selector';
import {
  type HistValue,
  PeriodPicker,
} from '@shared/ui-kit/components/period-picker';
import { Tooltip } from '@mui/material';
import { formatDate, IconButton } from '@shared/ui-kit';
import { type FC } from 'react';
import { BenchmarkSelector } from '../benchmark-selector';
import { useTranslation } from 'react-i18next';
import { usePortfoliosData } from '@shared/ui-kit';

export type ToolbarControl =
  | 'refresh'
  | 'benchmark'
  | 'accounts'
  | 'period'
  | 'reports';

export interface ToolbarProps
  extends Partial<Record<`${ToolbarControl}Disabled`, boolean>> {
  readonly controls?: Array<ToolbarControl>;
  readonly accounts?: AvailableOptions;
  readonly onChangeAccountSelected?: (account: string) => void;
  readonly accountSelected?: string;
  readonly grouping?: boolean;
  readonly onRefresh?: () => void;
  readonly benchmarkSelected?: string;
  readonly onBenchmarkSelect?: (benchmark: string) => void;
  readonly onPeriodChange?: (value: number) => void;
  readonly onDateChange?: (startDate: Date, endDate: Date) => void;
  readonly onDownloadReport?: () => void;
  readonly defaultHist?: HistValue;
  readonly availablePeriods?: HistValue[] | 'all';
  readonly showDatePicker?: boolean;
  readonly disabled?: boolean;
  readonly hasHoldings?: boolean;
  readonly updatedDate?: string;
}

export const Toolbar: FC<ToolbarProps> = ({
  accounts = [],
  onChangeAccountSelected = () => {
    return;
  },
  accountSelected,
  grouping,
  onRefresh,
  benchmarkSelected,
  onBenchmarkSelect,
  onPeriodChange,
  onDateChange,
  availablePeriods,
  defaultHist,
  showDatePicker,
  controls = ['refresh', 'benchmark', 'accounts', 'period', 'reports'],
  disabled,
  periodDisabled,
  accountsDisabled,
  refreshDisabled,
  benchmarkDisabled,
  reportsDisabled,
  hasHoldings,
  updatedDate,
  onDownloadReport,
}) => {
  const { t, i18n } = useTranslation();
  const { savedBenchmarks } = usePortfoliosData();

  return (
    <div
      className="flex flex-col gap-8 p-4"
      data-testid="aggregator-toolbar"
      aria-disabled={disabled}
    >
      <div className="flex items-center justify-between gap-x-4">
        <div className="flex items-center gap-x-4">
          {updatedDate && (
            <p className="pl-4 italic text-primary">
              {t('glossary.updated-on', {
                date: formatDate(updatedDate, 'long', i18n.language),
                ns: 'glossary',
              })}
            </p>
          )}
          {controls.includes('refresh') && (
            <Tooltip title={t('common.refresh', { ns: 'common' })} placement="top">
              <div
                className="flex items-center justify-center aria-disabled:cursor-not-allowed"
                aria-disabled={refreshDisabled || disabled}
                data-testid="refresh-button-container"
              >
                <IconButton
                  icon="RefreshTwoArrows"
                  size={24}
                  variant="icon"
                  color={disabled ? 'disabled' : 'main'}
                  onClick={onRefresh}
                  data-testid="refresh-btn"
                  disabled={refreshDisabled || disabled}
                />
              </div>
            </Tooltip>
          )}
        </div>

        {controls.includes('reports') && (
          <Tooltip
            title={t('common.download-report', { ns: 'common' })}
            placement="top"
          >
            <div
              className="flex items-center justify-center aria-disabled:cursor-not-allowed"
              aria-disabled={reportsDisabled || disabled || !hasHoldings}
              data-testid="aggregator-report-button-container"
            >
              <IconButton
                icon="Reporting"
                onClick={onDownloadReport}
                data-testid="aggregator-report-button"
                disabled={reportsDisabled || disabled || !hasHoldings}
              />
            </div>
          </Tooltip>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-4">
        <div className="flex items-center gap-x-9">
          {controls.includes('period') && (
            <PeriodPicker
              onValueChange={onPeriodChange}
              defaultValue={defaultHist}
              showDatePicker={showDatePicker}
              onDateChange={onDateChange}
              availableOptions={availablePeriods}
              disabled={periodDisabled || disabled}
            />
          )}

          {controls.includes('benchmark') && (
            <BenchmarkSelector
              onChange={onBenchmarkSelect}
              value={benchmarkSelected}
              disabled={benchmarkDisabled || disabled}
              savedBenchmarks={savedBenchmarks}
            />
          )}
        </div>

        {controls.includes('accounts') && (
          <div
            className="flex w-full max-w-72 aria-disabled:cursor-not-allowed"
            aria-disabled={accountsDisabled || disabled}
            data-testid="account-selector-container"
          >
            <OptionsSelector
              optionsAvailable={accounts}
              onChangeValueSelected={onChangeAccountSelected}
              defaultText={t('common.select-account', { ns: 'common' })}
              selectType="white"
              optionSelected={accountSelected}
              anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
              }}
              grouping={grouping}
              multiple={false}
              selectedAll={false}
              checkbox={false}
              defaultValue="ALL"
              dataTestId="account-selector"
              disabled={accountsDisabled || disabled || accounts.length === 0}
            />
          </div>
        )}
      </div>
    </div>
  );
};
